import { useEffect, useLayoutEffect } from 'react';

// externals
import { useDispatch, useSelector } from 'react-redux';
import type { SelectInputOption } from '@interstate/components/SelectInput';

// libraries
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';

// components
import { SelectInput } from './TradeInSelectInput';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

// constants/enums
import { TRADE_IN_MODEL } from '@makemydeal/dr-dash-components';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

interface MakeFieldProps {
    isManualFormOpen: boolean;
    disabled?: boolean;
}

const ModelField: React.FC<MakeFieldProps> = ({ isManualFormOpen, disabled }) => {
    const { tradeInIndex, tradeInValidator, tradeInFormErrors } = useSingleTradeInFormProvider();
    const dispatch = useDispatch();
    const vehicleYear = useSelector(tradeInsSelectors.getTradeYear(tradeInIndex));
    const vehicleMake = useSelector(tradeInsSelectors.getTradeMake(tradeInIndex));
    const vehicleMakeId = useSelector(tradeInsSelectors.getTradeMakeId(tradeInIndex));
    const vehicleModel = useSelector(tradeInsSelectors.getTradeModel(tradeInIndex));
    const vehicleModelId = useSelector(tradeInsSelectors.getTradeModelId(tradeInIndex));
    const tradeInVehicleModels = useSelector(tradeInsSelectors.getTradeInComponentModels(tradeInIndex));
    const tradeInVehicle = useSelector(tradeInsSelectors.getTradeInComponentVehicle(tradeInIndex));
    const hasMultipleVin = useSelector(tradeInsSelectors.hasMultipleVin(tradeInIndex));

    const modelValue = vehicleMake && vehicleModel !== 'default' ? vehicleModel : '';
    const normalizedModelValue =
        tradeInVehicleModels.options.find((option) => modelValue?.toLowerCase() === option.name.toLowerCase())?.name ?? modelValue;
    const isModelInputDisabled =
        hasMultipleVin || (tradeInVehicleModels.options.length === 0 && !modelValue) || !vehicleMake || vehicleMake === 'default';

    useLayoutEffect(() => {
        tradeInValidator.setValue('vehicle.model', modelValue || '', { validate: true, clearErrors: false });
    }, [modelValue, tradeInValidator]);

    useEffect(() => {
        const model = tradeInVehicleModels.options.find((option) => vehicleModel?.toLowerCase() === option.name.toLowerCase());

        if (model && vehicleModelId !== model.id) {
            dispatch(tradeInsActionCreators.updateTradeInModelId(model.id, tradeInIndex));
        }
    }, [tradeInVehicleModels, vehicleModel, vehicleModelId, dispatch]);

    useEffect(() => {
        if (vehicleYear && vehicleMakeId && vehicleMake && vehicleModel && vehicleModelId) {
            dispatch(
                tradeInsActionCreators.fetchTradeInComponentTrims(
                    MANAGER_VIEW_APP_PREFIX,
                    vehicleYear,
                    vehicleMakeId,
                    vehicleModelId,
                    tradeInIndex
                )
            );
        }
    }, [vehicleMakeId, vehicleModelId, vehicleYear, vehicleMake, vehicleModel, dispatch]);

    const getTradeInModels = (): SelectInputOption[] => {
        const modelsOptions =
            modelValue && !tradeInVehicleModels.options.find((model) => model.name.toLowerCase() === modelValue.toLowerCase())
                ? [...tradeInVehicleModels.options, { name: modelValue }]
                : tradeInVehicleModels.options;

        return modelsOptions.map((model) => ({ label: model.name, value: model.name })).sort();
    };

    const onModelChange = (event: any) => {
        const model = tradeInVehicleModels.options.find((model) => model.name === event.target.value);
        if (!model) return;
        const updatedVehicle = {
            ...tradeInVehicle,
            model,
            trim: { id: '', name: '', vehicleId: '' }
        };

        // tradeInValidator.setValues({ vehicle: { model: model.name, trim: '' } }, { mode: 'merge', validate: true });
        tradeInValidator.setValue('vehicle.model', model.name, { validate: true, clearErrors: false });
        tradeInValidator.setValue('vehicle.trim', '', { validate: true, clearErrors: false });
        tradeInValidator.commit(['vehicle.model', 'vehicle.trim'], { validate: true });

        dispatch(tradeInsActionCreators.updateTradeInModel(model, tradeInIndex));
        dispatch(tradeInsActionCreators.updateVehicle(updatedVehicle, tradeInIndex));
        if (model) {
            dispatch(
                tradeInsActionCreators.fetchTradeInComponentTrims(
                    MANAGER_VIEW_APP_PREFIX,
                    vehicleYear,
                    vehicleMakeId,
                    model.id,
                    tradeInIndex
                )
            );
        }
    };

    return (
        <>
            {isManualFormOpen && (
                <SelectInput
                    id="trade-models-select-input"
                    data-testid="trade-models-select-input"
                    label={TRADE_IN_MODEL}
                    name={TRADE_IN_MODEL}
                    onChange={onModelChange}
                    options={getTradeInModels()}
                    value={normalizedModelValue}
                    placeholder="Select"
                    disabled={isModelInputDisabled || disabled}
                    displayDeselectOption={false}
                    size="sm"
                    hasError={tradeInFormErrors.vehicle.model.hasError}
                    errorMessage={tradeInFormErrors.vehicle.model.errorMessage}
                />
            )}
        </>
    );
};

export default ModelField;
